import i18n from '../i18n';
import { REGEX_ALPHANUMERIC, REGEX_MAIL } from '@/constants/regex';
import dayjs from 'dayjs';

const basics = {
  required: (v: any) => {
    if (Array.isArray(v) && !v.length) {
      return false;
    }
    return v === 0 || !!v || i18n.t('Required');
  },
  requiredFile: (v: File) => {
    return v !== null || 'Error';
  },
  domesticMove: () => (v: string) => !v || /^LA CLEAR$/i.test(v) || `Only 'LA CLEAR' is allowed`,

  /** Allows 45 characters per line of textarea */
  cbpDescription: (v: string) => {
    const lines = v?.split('\n') ?? [];
    const incorrectLines = lines.reduce<number[]>((acc, line, index) => {
      if (line.length >= 45) acc.push(index + 1);
      return acc;
    }, []);
    return (
      !incorrectLines.length ||
      `CBP allows only 45 characters per line. Please resolve formatting issues in lines: ${incorrectLines.join(
        ', '
      )}`
    );
  },

  /**Only required if dependent value is truthy */
  requiredDepends: (d: any) => (v: any) => !d || v === 0 || !!v || i18n.t('Required'),
  requiredNumeric: (v: any) => Number(v) !== 0 || 'Must be more than 0',

  /**Only required if dependent value is false */
  requiredDependsOnFalsy: (d: any) => (v: any) => v === 0 || !!v || !!d || i18n.t('Required'),

  maxLength: (max: number) => (v: any) =>
    !v || v.length <= max || (i18n.t('@MaxLength') as string).replace('@max', String(max)),

  rangeLength: (min: number, max: number) => (v: string) =>
    !v || (v.length >= min && v.length <= max) || `Length range ${min}-${max}`,

  range: (min: number, max: number) => (v: any) => !v || (v >= min && v <= max) || `${min}-${max}`,

  biggerThan:
    (min: number, orEqual = false) =>
    (v: any) =>
      !v ||
      (orEqual ? v >= min : v > min) ||
      `Must be bigger than ${min}${orEqual ? ' or equal' : ''}`,

  validateIfNotNA(rules: ((value: string) => boolean | string)[]) {
    return (value: string) => {
      if (['NA', 'TBD'].includes(value)) {
        return true;
      }
      for (const rule of rules) {
        const result = rule(value);
        if (typeof result === 'string') {
          return result;
        }
      }
      return true;
    };
  },

  lessThan:
    (min: number, orEqual = false) =>
    (v: any) =>
      !v ||
      (orEqual ? v <= min : v < min) ||
      (i18n.t('@LessThan') as string).replace('@max', String(min)),

  requiredLength: (length: number) => (v: string) =>
    !v || length === v.length || `Required length ${length}`,
  containerNumber: () => (v: any) =>
    !v || /^[A-Za-z]{4}\d{7}$/.test(v) || `Please use this format AAAA9999999`,
  billOffLandingNumber: () => (v: any) =>
    !v ||
    /^[A-Za-z]{4}[a-zA-Z0-9]{4,12}$/.test(v) ||
    `First 4 symbols must be letters. Length 8-12`,
  precision: (precision: number) => (v: any) => {
    if (!v || v.toString().indexOf('.') === -1) {
      return true;
    }

    const split = v.toString().split('.');

    if (split.length > 2) {
      return (i18n.t('@AllowedPrecision') as string).replace('@val', String(precision));
    }

    return (
      split[split.length - 1].length <= precision ||
      (i18n.t('@AllowedPrecision') as string).replace('@val', String(precision))
    );
  },
  alphaNumeric: (v: string) => !v || !REGEX_ALPHANUMERIC.test(v) || `Only numeric and alphabetic`,
  alphaNumberAndSymbol: (v: any) =>
    !v || !/[^\dA-Za-z#\\-]+/g.test(v) || `Only numeric, alphabetic and '-#' symbols`,

  email: () => (v: any) => {
    if (!v) {
      return true;
    }
    const regex = new RegExp(REGEX_MAIL);
    return regex.test(v) || i18n.t('EmailMustBeValid');
  },

  numeric:
    (allowFloatingPoint = false) =>
    (v: any) =>
      !v ||
      (allowFloatingPoint ? !isNaN(v) : Number.isInteger(Number(v))) ||
      i18n.t('OnlyNumbersAreAllowed'),

  validateEmail: (emails = []) => {
    return emails.every((email) => {
      const result = basics.email()(email);
      return typeof result === 'string' ? false : result;
    });
  },
  dateRangeMaxRange: (maxRangeInDays: number) => (dateRange: string) => {
    if (!dateRange) {
      return true;
    }
    const dayFrom = dateRange?.split(' - ')?.[0];
    const dayTo = dateRange?.split(' - ')?.[1];

    const start = new Date(dayFrom);
    const end = new Date(dayTo);

    const diffDays = dayjs(end).diff(dayjs(start), 'day');

    return (
      diffDays < maxRangeInDays || i18n.t('NumberOfDaysCannotBeMoreThan', { max: maxRangeInDays })
    );
  },
};

export default basics;
